/**
 * Phase 14.5 — QorBOM Docs · Shared low-level building blocks.
 *
 * Extracted from the old single-file `QorbomApiDocs.jsx` so every
 * page in the new 3-column docs shell can reuse the same primitives:
 *
 *   • SectionHeader    — eyebrow + H2
 *   • Callout          — bordered card (renamed from FactCard for
 *                        the Stripe/Mintlify-style callout idiom)
 *   • NoticeBox        — amber/blue/rose left-border admonition
 *   • CodeBlock        — copy-to-clipboard <pre> with language hint
 *   • CodeTabs         — multi-tab CodeBlock (curl/Python/Go/Node)
 *
 * Visual contract matches the old surface so we don't break the
 * brand the user already approved on /qorbom/api-docs.
 */
import React, { useState } from "react";
import { Copy, Check } from "lucide-react";


export function SectionHeader({ eyebrow, title, id }) {
  return (
    <header className="mb-6">
      {eyebrow && (
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-blue-700"
             data-testid="qorbom-docs-eyebrow">
          {eyebrow}
        </div>
      )}
      <h1
        id={id}
        className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
        style={{ fontFamily: "Manrope, system-ui, sans-serif" }}
        data-testid="qorbom-docs-page-title"
      >
        {title}
      </h1>
    </header>
  );
}


export function Callout({ label, children, testid }) {
  return (
    <div
      className="rounded-md border border-slate-200 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
      data-testid={testid}
    >
      {label && (
        <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
          {label}
        </div>
      )}
      {children}
    </div>
  );
}


// Mintlify-style left-border admonition. `tone` picks the accent.
export function NoticeBox({ tone = "blue", title, children, testid }) {
  const tones = {
    blue:   { border: "border-blue-700",    bg: "bg-blue-50" },
    amber:  { border: "border-amber-500",   bg: "bg-amber-50" },
    rose:   { border: "border-rose-500",    bg: "bg-rose-50" },
    green:  { border: "border-emerald-600", bg: "bg-emerald-50" },
    slate:  { border: "border-slate-400",   bg: "bg-slate-50" },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div
      className={`rounded-md border-l-4 ${t.border} ${t.bg} p-4 text-sm text-slate-800`}
      data-testid={testid}
    >
      {title && <strong className="mr-1">{title}</strong>}
      {children}
    </div>
  );
}


export function CodeBlock({ lang, code, flat, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };
  return (
    <div className={"relative " + (flat ? "" : "mt-2 rounded-md border border-slate-200 bg-slate-50")}
         data-testid={testid}>
      <button
        onClick={copy}
        className="absolute right-3 top-3 rounded-md border border-slate-300 bg-white p-1.5 text-slate-500 transition-colors hover:text-slate-900"
        aria-label={`Copy ${lang || "code"}`}
        data-testid="qorbom-docs-copy-btn"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
      <pre
        className="overflow-x-auto p-4 text-xs leading-relaxed text-slate-800"
        style={{ fontFamily: '"IBM Plex Mono", monospace' }}
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}


export function CodeTabs({ tabs, testid }) {
  const [idx, setIdx] = useState(0);
  return (
    <div className="mt-6 overflow-hidden rounded-md border border-slate-200 bg-white"
         data-testid={testid}>
      <div className="flex gap-1 border-b border-slate-200 bg-slate-50 px-3 pt-3">
        {tabs.map((t, i) => (
          <button
            key={t.label}
            onClick={() => setIdx(i)}
            className={
              "rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors " +
              (i === idx
                ? "bg-white text-slate-900 border border-slate-200 border-b-white"
                : "text-slate-500 hover:text-slate-900")
            }
            data-testid={`qorbom-docs-codetab-${t.label.toLowerCase()}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <CodeBlock lang={tabs[idx].lang} code={tabs[idx].code} flat />
    </div>
  );
}
